import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  Table,
  TableRow,
  TableCell,
  WidthType,
  ImageRun,
  ExternalHyperlink,
  HorizontalPositionAlign,
  HorizontalPositionRelativeFrom,
  VerticalPositionRelativeFrom,
  TextWrappingType,
  TextWrappingSide,
  ShadingType,
} from 'docx';

type ParagraphChild = TextRun | ImageRun | ExternalHyperlink;

/**
 * Converte spans data-variable de volta para texto plano {variavel}.
 * Necessário para compatibilidade com o backend (LocacaoService.GerarContrato).
 */
function convertNodesToVariables(html: string): string {
  return html.replace(
    /<span[^>]*data-variable="([^"]+)"[^>]*>[^<]*<\/span>/gi,
    (_, name) => `{${name}}`
  );
}

interface TextStyle {
  bold?: boolean;
  italics?: boolean;
  underline?: boolean;
  strike?: boolean;
  color?: string;
  font?: string;
  link?: string;
  fontSize?: number; // half-points (24 = 12pt)
  highlight?: string; // hex sem # (background-color)
}

interface ImagePositioning {
  align?: 'left' | 'center' | 'right' | null;
  float?: 'left' | 'right' | null;
  posX?: number | null;
  posY?: number | null;
}

type RunItem =
  | ({ kind: 'text'; text: string } & TextStyle)
  | ({ kind: 'image'; src: string; width?: number; height?: number; alt?: string } & TextStyle & ImagePositioning)
  | { kind: 'break' };

function pxToTwips(px: number): number {
  return Math.round(px * 15);
}

function colorToHex(color: string): string | undefined {
  if (/^#?[0-9a-fA-F]{6}$/.test(color)) {
    return color.replace('#', '').toUpperCase();
  }
  const m = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (m) {
    const r = parseInt(m[1]).toString(16).padStart(2, '0');
    const g = parseInt(m[2]).toString(16).padStart(2, '0');
    const b = parseInt(m[3]).toString(16).padStart(2, '0');
    return `${r}${g}${b}`.toUpperCase();
  }
  return undefined;
}

function dataUrlToBytes(dataUrl: string): { data: Uint8Array; type: 'png' | 'jpg' | 'gif' | 'bmp' } | null {
  const match = dataUrl.match(/^data:image\/([a-z+]+);base64,(.+)$/i);
  if (!match) return null;
  const subtype = match[1].toLowerCase();
  const base64 = match[2];
  let type: 'png' | 'jpg' | 'gif' | 'bmp';
  if (subtype === 'png') type = 'png';
  else if (subtype === 'jpeg' || subtype === 'jpg') type = 'jpg';
  else if (subtype === 'gif') type = 'gif';
  else if (subtype === 'bmp') type = 'bmp';
  else return null;

  try {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return { data: bytes, type };
  } catch {
    return null;
  }
}

function parseInlineContent(element: Element): RunItem[] {
  const items: RunItem[] = [];

  function walk(node: Node, inherited: TextStyle) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (text) items.push({ kind: 'text', text, ...inherited });
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node as Element;
    const tag = el.tagName.toLowerCase();

    if (tag === 'br') {
      items.push({ kind: 'break' });
      return;
    }

    if (tag === 'img') {
      const src = el.getAttribute('src');
      if (src) {
        const w = parseInt(el.getAttribute('width') || '');
        const h = parseInt(el.getAttribute('height') || '');
        const align = el.getAttribute('data-align') as 'left' | 'center' | 'right' | null;
        const float = el.getAttribute('data-float') as 'left' | 'right' | null;
        const posXAttr = el.getAttribute('data-pos-x');
        const posYAttr = el.getAttribute('data-pos-y');
        const posX = posXAttr != null ? parseFloat(posXAttr) : null;
        const posY = posYAttr != null ? parseFloat(posYAttr) : null;
        items.push({
          kind: 'image',
          src,
          width: isFinite(w) && w > 0 ? w : undefined,
          height: isFinite(h) && h > 0 ? h : undefined,
          alt: el.getAttribute('alt') || undefined,
          align,
          float,
          posX: posX != null && isFinite(posX) ? posX : null,
          posY: posY != null && isFinite(posY) ? posY : null,
          ...inherited,
        });
      }
      return;
    }

    const style: TextStyle = { ...inherited };
    if (tag === 'strong' || tag === 'b') style.bold = true;
    if (tag === 'em' || tag === 'i') style.italics = true;
    if (tag === 'u') style.underline = true;
    if (tag === 's' || tag === 'del') style.strike = true;
    if (tag === 'mark') {
      const bg = (el as HTMLElement).style?.backgroundColor || el.getAttribute('data-color') || '';
      const hex = bg ? colorToHex(bg) : undefined;
      if (hex) style.highlight = hex;
    }
    if (tag === 'a') {
      const href = el.getAttribute('href');
      if (href) style.link = href;
    }

    const inlineStyle = (el as HTMLElement).style;
    if (inlineStyle?.color) style.color = inlineStyle.color;
    if (inlineStyle?.fontFamily) style.font = inlineStyle.fontFamily.replace(/['"]/g, '');
    if (inlineStyle?.backgroundColor) {
      const hex = colorToHex(inlineStyle.backgroundColor);
      if (hex) style.highlight = hex;
    }
    if (inlineStyle?.fontSize) {
      // CSS font-size pode vir em px ou pt. Converte para half-points (Word).
      const raw = inlineStyle.fontSize.trim();
      const num = parseFloat(raw);
      if (isFinite(num) && num > 0) {
        if (raw.endsWith('pt')) style.fontSize = Math.round(num * 2);
        else if (raw.endsWith('px')) style.fontSize = Math.round(num * 1.5);
        // ignora em/rem/% (raros no editor)
      }
    }

    for (const child of Array.from(node.childNodes)) {
      walk(child, style);
    }
  }

  for (const child of Array.from(element.childNodes)) {
    walk(child, {});
  }

  return items;
}

function makeTextRun(item: Extract<RunItem, { kind: 'text' }>, leadingBreak = 0): TextRun {
  return new TextRun({
    text: item.text,
    bold: item.bold,
    italics: item.italics,
    underline: item.underline ? {} : undefined,
    strike: item.strike,
    color: item.color ? colorToHex(item.color) : undefined,
    font: item.font || undefined,
    size: item.fontSize,
    shading: item.highlight
      ? { type: ShadingType.CLEAR, color: 'auto', fill: item.highlight }
      : undefined,
    break: leadingBreak > 0 ? leadingBreak : undefined,
  });
}

function makeImageRun(item: Extract<RunItem, { kind: 'image' }>): ParagraphChild {
  if (!item.src.startsWith('data:')) {
    return new TextRun({ text: item.alt || '[imagem externa]' });
  }
  const decoded = dataUrlToBytes(item.src);
  if (!decoded) return new TextRun({ text: item.alt || '[imagem]' });

  const width = item.width || 300;
  const height = item.height || 200;

  // Posicionamento livre (drag): floating absoluto em relação à página.
  const hasFreePosition =
    item.posX != null && isFinite(item.posX) && item.posY != null && isFinite(item.posY);
  if (hasFreePosition) {
    const PX_TO_EMU = 9525;
    return new ImageRun({
      data: decoded.data,
      type: decoded.type,
      transformation: { width, height },
      floating: {
        horizontalPosition: {
          relative: HorizontalPositionRelativeFrom.PAGE,
          offset: Math.round((item.posX as number) * PX_TO_EMU),
        },
        verticalPosition: {
          relative: VerticalPositionRelativeFrom.PAGE,
          offset: Math.round((item.posY as number) * PX_TO_EMU),
        },
        wrap: { type: TextWrappingType.SQUARE, side: TextWrappingSide.BOTH_SIDES },
        margins: { top: 0, bottom: 0, left: 114300, right: 114300 },
      },
    });
  }

  // Float left/right: ancora no parágrafo, alinha à margem.
  if (item.float === 'left' || item.float === 'right') {
    return new ImageRun({
      data: decoded.data,
      type: decoded.type,
      transformation: { width, height },
      floating: {
        horizontalPosition: {
          relative: HorizontalPositionRelativeFrom.MARGIN,
          align: item.float === 'left' ? HorizontalPositionAlign.LEFT : HorizontalPositionAlign.RIGHT,
        },
        verticalPosition: {
          relative: VerticalPositionRelativeFrom.PARAGRAPH,
          offset: 0,
        },
        wrap: { type: TextWrappingType.SQUARE, side: TextWrappingSide.BOTH_SIDES },
        margins: { top: 0, bottom: 0, left: 114300, right: 114300 },
      },
    });
  }

  return new ImageRun({
    data: decoded.data,
    transformation: { width, height },
    type: decoded.type,
  });
}

/**
 * Agrupa itens consecutivos com o mesmo link em ExternalHyperlink.
 * Quebras (<br>) viram `break: 1` no próximo TextRun.
 */
function itemsToParagraphChildren(items: RunItem[]): ParagraphChild[] {
  const out: ParagraphChild[] = [];
  let pendingBreaks = 0;
  let i = 0;

  while (i < items.length) {
    const item = items[i];

    if (item.kind === 'break') {
      pendingBreaks += 1;
      i++;
      continue;
    }

    const link = item.kind === 'text' || item.kind === 'image' ? item.link : undefined;

    if (link) {
      const linkChildren: TextRun[] = [];
      const linkImages: ImageRun[] = [];
      let firstBreaks = pendingBreaks;
      pendingBreaks = 0;

      while (i < items.length) {
        const cur = items[i];
        if (cur.kind === 'break') {
          if (linkChildren.length > 0 || linkImages.length > 0) {
            // break dentro do hyperlink: insere TextRun vazio com break
            linkChildren.push(new TextRun({ text: '', break: 1 }));
            i++;
            continue;
          }
          break;
        }
        const curLink = (cur.kind === 'text' || cur.kind === 'image') ? cur.link : undefined;
        if (curLink !== link) break;

        if (cur.kind === 'text') {
          const tr = makeTextRun(cur, firstBreaks);
          firstBreaks = 0;
          linkChildren.push(tr);
        } else if (cur.kind === 'image') {
          // ImageRun fora do hyperlink (docx aceita imagem como filha mas o tipo de
          // ExternalHyperlink.children é (TextRun | ImageRun)[]). Por simplicidade,
          // empilhamos a imagem como sibling.
          const img = makeImageRun(cur);
          if (img instanceof ImageRun) linkImages.push(img);
          else linkChildren.push(img as TextRun);
        }
        i++;
      }

      if (linkChildren.length > 0) {
        out.push(new ExternalHyperlink({ link, children: linkChildren }));
      }
      for (const img of linkImages) out.push(img);
      continue;
    }

    if (item.kind === 'text') {
      out.push(makeTextRun(item, pendingBreaks));
      pendingBreaks = 0;
    } else if (item.kind === 'image') {
      // breaks pendentes antes de imagem: insere um TextRun com breaks
      if (pendingBreaks > 0) {
        out.push(new TextRun({ text: '', break: pendingBreaks }));
        pendingBreaks = 0;
      }
      out.push(makeImageRun(item));
    }
    i++;
  }

  // breaks pendentes no final
  if (pendingBreaks > 0) {
    out.push(new TextRun({ text: '', break: pendingBreaks }));
  }

  return out;
}

function getAlignment(el: Element): typeof AlignmentType[keyof typeof AlignmentType] | undefined {
  const style = (el as HTMLElement).style?.textAlign;
  if (style === 'center') return AlignmentType.CENTER;
  if (style === 'right') return AlignmentType.RIGHT;
  if (style === 'justify') return AlignmentType.JUSTIFIED;
  if (style === 'left') return AlignmentType.LEFT;
  return undefined;
}

function getParagraphSpacingAndIndent(el: Element): { spacing?: any; indent?: any } {
  const style = (el as HTMLElement).style;
  if (!style) return {};

  const spacing: any = {};
  const indent: any = {};

  const marginTop = parseFloat(style.marginTop);
  if (isFinite(marginTop) && marginTop > 0) spacing.before = pxToTwips(marginTop);

  const marginBottom = parseFloat(style.marginBottom);
  if (isFinite(marginBottom) && marginBottom > 0) spacing.after = pxToTwips(marginBottom);

  if (style.lineHeight) {
    const lh = style.lineHeight.trim();
    if (lh.endsWith('px')) {
      const px = parseFloat(lh);
      if (isFinite(px) && px > 0) {
        spacing.line = pxToTwips(px);
        spacing.lineRule = 'exact';
      }
    } else {
      const factor = parseFloat(lh);
      if (isFinite(factor) && factor > 0) {
        spacing.line = Math.round(factor * 240);
        spacing.lineRule = 'auto';
      }
    }
  }

  const marginLeft = parseFloat(style.marginLeft);
  if (isFinite(marginLeft) && marginLeft > 0) indent.left = pxToTwips(marginLeft);

  const marginRight = parseFloat(style.marginRight);
  if (isFinite(marginRight) && marginRight > 0) indent.right = pxToTwips(marginRight);

  const textIndent = parseFloat(style.textIndent);
  if (isFinite(textIndent)) {
    if (textIndent > 0) indent.firstLine = pxToTwips(textIndent);
    else if (textIndent < 0) indent.hanging = pxToTwips(-textIndent);
  }

  const result: any = {};
  if (Object.keys(spacing).length > 0) result.spacing = spacing;
  if (Object.keys(indent).length > 0) result.indent = indent;
  return result;
}

function alignFromImgChild(node: Element): typeof AlignmentType[keyof typeof AlignmentType] | undefined {
  // Quando a imagem é centralizada/alinhada via toolbar do editor, o TipTap grava
  // data-align na <img> e NÃO no <p>. Se o parágrafo só contém a imagem, herda o
  // alinhamento dela para que o DOCX gerado fique igual ao editor.
  const imgs = Array.from(node.querySelectorAll('img'));
  if (imgs.length === 0) return undefined;
  // Ignorar imagens floating/free-positioned: já têm seu próprio anchor.
  const targets = imgs.filter(
    (i) => !i.getAttribute('data-float') && i.getAttribute('data-pos-x') == null,
  );
  if (targets.length === 0) return undefined;
  const align = targets[0].getAttribute('data-align');
  if (align === 'left') return AlignmentType.LEFT;
  if (align === 'center') return AlignmentType.CENTER;
  if (align === 'right') return AlignmentType.RIGHT;
  return undefined;
}

function buildParagraph(
  node: Element,
  opts: {
    heading?: typeof HeadingLevel[keyof typeof HeadingLevel];
    bullet?: { level: number };
    numbering?: { reference: string; level: number };
  } = {}
): Paragraph {
  const items = parseInlineContent(node);
  const children = itemsToParagraphChildren(items);
  const { spacing, indent } = getParagraphSpacingAndIndent(node);
  const alignment = getAlignment(node) ?? alignFromImgChild(node);

  return new Paragraph({
    heading: opts.heading,
    alignment,
    bullet: opts.bullet,
    numbering: opts.numbering,
    spacing,
    indent,
    children: children.length > 0 ? children : [new TextRun('')],
  });
}

function parseTableElement(tableEl: Element): Table {
  const rows: TableRow[] = [];

  const trElements = tableEl.querySelectorAll('tr');
  for (const tr of Array.from(trElements)) {
    const cells: TableCell[] = [];
    const tdElements = tr.querySelectorAll('td, th');

    for (const td of Array.from(tdElements)) {
      // Cada parágrafo da célula é um Paragraph próprio (preserva quebras de
      // múltiplos parágrafos dentro de uma célula).
      const cellParagraphs: Paragraph[] = [];
      const blocks = Array.from(td.children).filter((c) =>
        ['p', 'div', 'h1', 'h2', 'h3', 'h4'].includes(c.tagName.toLowerCase())
      );

      if (blocks.length > 0) {
        for (const block of blocks) {
          cellParagraphs.push(buildParagraph(block));
        }
      } else {
        cellParagraphs.push(buildParagraph(td));
      }

      cells.push(
        new TableCell({
          children: cellParagraphs,
          width: { size: 100 / tdElements.length, type: WidthType.PERCENTAGE },
        })
      );
    }

    if (cells.length > 0) {
      rows.push(new TableRow({ children: cells }));
    }
  }

  return new Table({
    rows,
    width: { size: 100, type: WidthType.PERCENTAGE },
  });
}

/**
 * Promove imagens floating (data-pos-x/y, data-float) que estejam aninhadas
 * em <p>, <div>, etc para o nível raiz. Isso garante que cada imagem floating
 * vire seu próprio Paragraph no DOCX (anchor independente) e não fique
 * "afogada" inline em um parágrafo de texto.
 */
function promoteFloatingImages(root: Element) {
  const all = Array.from(root.querySelectorAll('img'));
  for (const img of all) {
    const isFloating =
      img.hasAttribute('data-pos-x') ||
      img.hasAttribute('data-pos-y') ||
      img.getAttribute('data-float') === 'left' ||
      img.getAttribute('data-float') === 'right';
    if (!isFloating) continue;
    if (img.parentElement === root) continue;

    let host: Element | null = img.parentElement;
    while (host && host.parentElement !== root) host = host.parentElement;
    if (!host || !host.parentElement) continue;

    host.parentElement.insertBefore(img, host.nextSibling);
  }
}

/**
 * Converte o HTML do editor TipTap em elementos docx.
 */
function htmlToDocxElements(html: string): (Paragraph | Table)[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild || doc.body;
  promoteFloatingImages(root);
  const elements: (Paragraph | Table)[] = [];

  function processNode(node: Element) {
    const tag = node.tagName.toLowerCase();

    if (tag === 'table') {
      elements.push(parseTableElement(node));
      return;
    }

    if (tag === 'h1' || tag === 'h2' || tag === 'h3' || tag === 'h4') {
      const levelMap: Record<string, typeof HeadingLevel[keyof typeof HeadingLevel]> = {
        h1: HeadingLevel.HEADING_1,
        h2: HeadingLevel.HEADING_2,
        h3: HeadingLevel.HEADING_3,
        h4: HeadingLevel.HEADING_4,
      };
      elements.push(buildParagraph(node, { heading: levelMap[tag] }));
      return;
    }

    if (tag === 'ul' || tag === 'ol') {
      const items = node.querySelectorAll(':scope > li');
      for (const li of Array.from(items)) {
        elements.push(
          buildParagraph(li, {
            bullet: tag === 'ul' ? { level: 0 } : undefined,
            numbering: tag === 'ol' ? { reference: 'default-numbering', level: 0 } : undefined,
          })
        );
      }
      return;
    }

    if (tag === 'p' || tag === 'div') {
      elements.push(buildParagraph(node));
      return;
    }

    if (tag === 'img') {
      const src = node.getAttribute('src');
      if (!src) return;

      const w = parseInt(node.getAttribute('width') || '');
      const h = parseInt(node.getAttribute('height') || '');
      const width = isFinite(w) && w > 0 ? w : 300;
      const height = isFinite(h) && h > 0 ? h : 200;
      const alt = node.getAttribute('alt') || undefined;
      const align = node.getAttribute('data-align');
      const float = node.getAttribute('data-float');
      const posXAttr = node.getAttribute('data-pos-x');
      const posYAttr = node.getAttribute('data-pos-y');
      const posX = posXAttr != null ? parseFloat(posXAttr) : null;
      const posY = posYAttr != null ? parseFloat(posYAttr) : null;
      const hasFreePosition = posX != null && isFinite(posX) && posY != null && isFinite(posY);

      if (!src.startsWith('data:')) {
        elements.push(
          new Paragraph({ children: [new TextRun({ text: alt || '[imagem externa]' })] })
        );
        return;
      }
      const decoded = dataUrlToBytes(src);
      if (!decoded) {
        elements.push(new Paragraph({ children: [new TextRun({ text: alt || '[imagem]' })] }));
        return;
      }

      // Posição livre (drag para qualquer pixel): anchor com offset absoluto em
      // relação à página, mantendo wrapSquare para o texto contornar.
      if (hasFreePosition) {
        const PX_TO_EMU = 9525;
        const imageRun = new ImageRun({
          data: decoded.data,
          type: decoded.type,
          transformation: { width, height },
          floating: {
            horizontalPosition: {
              relative: HorizontalPositionRelativeFrom.PAGE,
              offset: Math.round((posX as number) * PX_TO_EMU),
            },
            verticalPosition: {
              relative: VerticalPositionRelativeFrom.PAGE,
              offset: Math.round((posY as number) * PX_TO_EMU),
            },
            wrap: {
              type: TextWrappingType.SQUARE,
              side: TextWrappingSide.BOTH_SIDES,
            },
            margins: { top: 0, bottom: 0, left: 114300, right: 114300 },
          },
        });
        elements.push(new Paragraph({ children: [imageRun] }));
        return;
      }

      if (float === 'left' || float === 'right') {
        const imageRun = new ImageRun({
          data: decoded.data,
          type: decoded.type,
          transformation: { width, height },
          floating: {
            horizontalPosition: {
              relative: HorizontalPositionRelativeFrom.MARGIN,
              align:
                float === 'left'
                  ? HorizontalPositionAlign.LEFT
                  : HorizontalPositionAlign.RIGHT,
            },
            verticalPosition: {
              relative: VerticalPositionRelativeFrom.PARAGRAPH,
              offset: 0,
            },
            wrap: {
              type: TextWrappingType.SQUARE,
              side: TextWrappingSide.BOTH_SIDES,
            },
            margins: { top: 0, bottom: 0, left: 114300, right: 114300 },
          },
        });
        elements.push(new Paragraph({ children: [imageRun] }));
        return;
      }

      const alignment =
        align === 'left'
          ? AlignmentType.LEFT
          : align === 'right'
          ? AlignmentType.RIGHT
          : align === 'center'
          ? AlignmentType.CENTER
          : getAlignment(node);

      elements.push(
        new Paragraph({
          alignment,
          children: [
            new ImageRun({
              data: decoded.data,
              type: decoded.type,
              transformation: { width, height },
            }),
          ],
        })
      );
      return;
    }

    if (tag === 'br') {
      elements.push(new Paragraph({ children: [] }));
      return;
    }

    // Fallback: processar filhos
    for (const child of Array.from(node.children)) {
      processNode(child);
    }
  }

  for (const child of Array.from(root.children)) {
    processNode(child);
  }

  // Se não parseou nada, retorna parágrafo vazio
  if (elements.length === 0) {
    elements.push(new Paragraph({ children: [new TextRun('')] }));
  }

  return elements;
}

/**
 * Exporta HTML do editor para um Blob DOCX.
 * As variáveis são convertidas de volta para texto plano {nome_variavel}
 * para compatibilidade com o string.Replace do backend.
 */
export async function exportDocx(html: string): Promise<Blob> {
  const plainTextHtml = convertNodesToVariables(html);
  const docElements = htmlToDocxElements(plainTextHtml);

  const doc = new Document({
    numbering: {
      config: [
        {
          reference: 'default-numbering',
          levels: [
            {
              level: 0,
              format: 'decimal' as any,
              text: '%1.',
              alignment: AlignmentType.LEFT,
            },
          ],
        },
      ],
    },
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: 1440,
              right: 1440,
              bottom: 1440,
              left: 1440,
            },
          },
        },
        children: docElements,
      },
    ],
  });

  return Packer.toBlob(doc);
}
